import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  MoreVert,
  Add,
  Search,
  ArrowBackIosNewRounded,
  ArrowForwardIosRounded,
  Download,
  Visibility,
} from "@mui/icons-material";
import adminLoginBg from "../../assets/images/background.png"; // adjust path
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useParams, useNavigate } from "react-router-dom";
import { useApi } from "../../api/useApi";

const ROWS_PER_PAGE = 10;

export function formattedDate (dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
}

export default function ViewReports() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const { domain } = useParams();
  const {request, loading} = useApi();
  const navigate = useNavigate();
  

  const [notif, setNotif] = useState({
    open: false,
    severity: "success",
    text: "",
  });

  useEffect(() => {
  const fetchReports = async () => {
    try {
      const reportList = await request({ url: `/evaluations/scores-with-roles` });

      if (reportList.length > 0) {
        const scoreObj = await request({
          url: `/evaluations/user/${reportList[0].emailId}/domain/${domain}/score`,
        });

        const updatedList = reportList.map((item, idx) =>
          idx === 0 ? { ...item, score: scoreObj.score } : item
        );

        const formattedData = 
            updatedList.map(report => {
                return {
                    id:report.id,
                    evalId: report.evaluationId,
                    email: report.emailId,
                    user: report.userName,
                    domain: report.domain,
                    score: Number(report.totalScore).toFixed(2),
                    level: report.level,
                    status: report.status,
                    date: formattedDate(report.createdAt),
                    role: report.userRole,
                }

            })

        setRows(formattedData);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  fetchReports();
}, [domain]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter(
      (r) =>
        r.user.toLowerCase().includes(s) ||
        r.email.toLowerCase().includes(s) ||
        r.role.toLowerCase().includes(s) ||
        r.domain.toLowerCase().includes(s)
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paginated = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return filtered.slice(start, start + ROWS_PER_PAGE);
  }, [filtered, page]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected((prev) => [
        ...prev,
        ...paginated.map((r) => r.id).filter((id) => !prev.includes(id)),
      ]);
    } else {
      setSelected((prev) =>
        prev.filter((id) => !paginated.some((r) => r.id === id))
      );
    }
  };

  const handleSelectOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDownload = () => {
    if (!rows || rows.length === 0) {
      setNotif({ open: true, severity: "error", text: "No data to download" });
      return;
    }
    try {
      const worksheetData = filtered.map((r) => ({
        User: r.user,
        Email: r.email,
        Role: r.role,
        Domain: r.domain,
        Score: r.score,
        Date: r.date,
      }));
      const ws = XLSX.utils.json_to_sheet(worksheetData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buf], { type: "application/octet-stream" });
      saveAs(blob, `report_export.xlsx`);
      setNotif({ open: true, severity: "success", text: "Report downloaded" });
    } catch (err) {
      console.error(err);
      setNotif({ open: true, severity: "error", text: "Export failed" });
    }
  };

  const renderPageButtons = () => {
    const pages = [];
    const maxShow = 9;
    if (totalPages <= maxShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, 2, 3, 4, "dots");
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    }
    return pages.map((p, idx) =>
      p === "dots" ? (
        <Typography key={`dots-${idx}`} sx={{ color: "whitesmoke", mx: 1 }}>
          ...
        </Typography>
      ) : (
        <Button
          key={p}
          onClick={() => setPage(p)}
          sx={{
            minWidth: 34,
            color: "whitesmoke",
            borderRadius: 0,
            px: 1,
          }}
        >
          <Typography
            sx={{
              borderBottom: page === p ? "3px solid whitesmoke" : "none",
              pb: 0.4,
            }}
          >
            {p}
          </Typography>
        </Button>
      )
    );
  };

  const handleView = (email,domain) => {
    navigate(`/admin/${domain}/view-reports/${email}`);
  }

  return (
    <Grid
      container
      component="main"
      sx={{
        minHeight: "90vh",
        position: "relative",
        justifyContent: "center",
        alignItems: "center",
        p: 3,
      }}
    >
      {/* Background */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${adminLoginBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.2,
          zIndex: -3,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "conic-gradient(from -7.72deg at 100% 100%, #000000 0deg, #49454F 294.45deg, #000000 360deg)",
          opacity: 1,
          backdropFilter: "blur(12px) saturate(180%)",
          zIndex: -4,
        }}
      />

      <Paper
        elevation={8}
        sx={{
          width: "77vw",
          maxWidth: "95vw",
          minHeight: { xs: "80vh", md: "78vh" },
          borderRadius: 4,
          p: 3,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.04))",
          boxShadow: "0 18px 40px rgba(0,0,0,0.6)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ color: "whitesmoke", mr: 2 }}>
            View Report:
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: "#48F1E0", fontWeight: 700, mr: "auto" }}
          >
            {domain.toLocaleUpperCase()}
          </Typography>
        </Box>

        {/* Search + Actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, }}>
          <TextField
            placeholder="What are you looking for today?"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            slotProps={{
                input:{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                }
            }}
            sx={{
              backgroundColor: "rgba(255,255,255,0.98)",
              borderRadius: 6,
              input: { py: 1 },
              boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
              flex: 1,
            }}
          />
            <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownload}
            sx={{
              borderRadius: 10,
              textTransform: "none",
              px: 3,
              background: "linear-gradient(90deg,#0B6BFF,#2B9EFF)",
              boxShadow: "0 6px 16px rgba(43,158,255,0.28)",
              flex: 0.3,
            }}
          >
            Download Report
          </Button>
          <IconButton sx={{ ml: 1, backgroundColor: "white"}}>
            <MoreVert />
          </IconButton>
          
        </Box>

        {/* Table */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            overflow: "auto", // ✅ Scroll here
            background: "rgba(255,255,255,0.92)",
            flex: 1,
            "&::-webkit-scrollbar": { height: 6, width: 6 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 12,
              background: "rgba(0,0,0,0.12)",
            },
          }}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      paginated.length > 0 &&
                      paginated.every((r) => selected.includes(r.id))
                    }
                    indeterminate={
                      paginated.some((r) => selected.includes(r.id)) &&
                      !paginated.every((r) => selected.includes(r.id))
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>User</TableCell>
                <TableCell colSpan={2}>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Domain</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} sx={{ height: "40vh" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                paginated.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(r.id)}
                        onChange={() => handleSelectOne(r.id)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{r.user}</TableCell>
                    <TableCell colSpan={2}>{r.email}</TableCell>
                    <TableCell>{r.role}</TableCell>
                    <TableCell>{r.domain}</TableCell>
                    <TableCell>{r.score}</TableCell>
                    <TableCell>{r.date}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleView(r.email,r.domain)}
                        sx={{
                            ml:0,
                            pl:0,
                        }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

              {!loading && paginated.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 2,
          }}
        >
          <IconButton
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ArrowBackIosNewRounded sx={{ color: "whitesmoke" }} />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", mx: 1 }}>
            {renderPageButtons()}
          </Box>

          <IconButton
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            <ArrowForwardIosRounded sx={{ color: "whitesmoke" }} />
          </IconButton>
        </Box>
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={notif.open}
        autoHideDuration={3000}
        onClose={() => setNotif((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={notif.severity}
          onClose={() => setNotif((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {notif.text}
        </Alert>
      </Snackbar>
    </Grid>
  );
}